import { ChartBarIcon } from "@phosphor-icons/react";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { StatTile } from "@bednbody/ui/components/stat-tile";

import { GIFTPASS_VALUE_PHP } from "@bednbody/logic";
import { GIFTPASS_BATCH_MAX } from "@bednbody/types";
import { Button } from "@bednbody/ui/components/button";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { ImportPrintedDialog } from "./ImportPrintedDialog";
import { giftpassCsv, giftpassCsvName } from "./giftpass.csv";
import {
  useCreateGiftPassBatch,
  useGiftPassBatches,
  useGiftPasses,
  useInvalidateBatch,
  useReactivatePass,
} from "./giftpass.hooks";

const EMPTY_FORM = { company: "", campaign: "", count: "" };

type Batch = NonNullable<ReturnType<typeof useGiftPassBatches>["data"]>[number];
type Pass = NonNullable<ReturnType<typeof useGiftPasses>["data"]>[number];

export function GiftPassPage() {
  const { data: batches, isLoading: batchesLoading } = useGiftPassBatches();
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const { data: passes, isLoading: passesLoading } = useGiftPasses(selectedBatch);
  const invalidateBatch = useInvalidateBatch();
  const reactivatePass = useReactivatePass();
  const createBatch = useCreateGiftPassBatch();
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [importing, setImporting] = useState(false);
  const isSuper = useAuth().claims?.role === "superadmin";

  const count = Number(form.count);
  const formValid =
    (form.company.trim() !== "" || form.campaign.trim() !== "") &&
    Number.isInteger(count) &&
    count >= 1 &&
    count <= GIFTPASS_BATCH_MAX;

  const openGenerate = () => {
    createBatch.reset();
    setForm(EMPTY_FORM);
    setGenerating(true);
  };

  const submitGenerate = () => {
    if (!formValid) return;
    createBatch.mutate(
      { company: form.company.trim(), campaign: form.campaign.trim(), count },
      {
        onSuccess: (result) => {
          setGenerating(false);
          // Straight to the new passes — the next step is printing them.
          setSelectedBatch(result.batchId);
        },
      },
    );
  };

  const selectedLabel = batches?.find((b) => b.id === selectedBatch)?.name ?? "batch";

  const downloadCodes = (matching: Pass[]) => {
    if (matching.length === 0) return;
    const blob = new Blob([giftpassCsv(matching)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = giftpassCsvName(selectedLabel);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleInvalidateBatch = (batchId: string) => {
    if (confirm("Are you sure you want to invalidate this batch? This cannot be undone.")) {
      invalidateBatch.mutate(batchId);
    }
  };

  const handleReactivatePass = (passId: string) => {
    reactivatePass.mutate(passId);
  };

  const batchColumns: ColumnDef<Batch, unknown>[] = [
    {
      id: "name",
      accessorFn: (batch) => batch.name,
      header: "Batch",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.name}
          subtitle={
            row.original.createdAt
              ? new Date(row.original.createdAt).toLocaleDateString("en-PH")
              : "Date unknown"
          }
          fallback="GP"
        />
      ),
    },
    { id: "total", accessorFn: (batch) => batch.totalPasses, header: "Total passes" },
    { id: "active", accessorFn: (batch) => batch.active, header: "Active" },
    {
      id: "status",
      accessorFn: (batch) => batch.status,
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={row.original.status === "active" ? "success" : "secondary"}
          className="capitalize"
        >
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: "created",
      accessorFn: (batch) => batch.createdAt ?? "",
      header: "Created",
      cell: ({ row }) =>
        row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString("en-PH") : "—",
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.name}
          actions={[
            { label: "View passes", onSelect: () => setSelectedBatch(row.original.id) },
            ...(row.original.status === "active"
              ? [
                  {
                    label: "Invalidate batch",
                    onSelect: () => handleInvalidateBatch(row.original.id),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];
  const passColumns: ColumnDef<Pass, unknown>[] = [
    {
      id: "code",
      accessorFn: (pass) => pass.display_code,
      header: "Pass",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.display_code}
          subtitle={selectedLabel}
          fallback="GP"
        />
      ),
    },
    {
      id: "status",
      accessorFn: (pass) => pass.status,
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.status === "active"
              ? "success"
              : row.original.status === "redeemed"
                ? "warning"
                : "secondary"
          }
          className="capitalize"
        >
          {row.original.status.replaceAll("_", " ")}
        </Badge>
      ),
    },
    {
      id: "used",
      accessorFn: (pass) => (pass.status === "redeemed" ? 1 : 0),
      header: "Used",
      cell: ({ row }) => (row.original.status === "redeemed" ? "Yes" : "No"),
    },
    {
      id: "redeemed",
      accessorFn: (pass) => pass.redeemed_at ?? "",
      header: "Used at",
      cell: ({ row }) =>
        row.original.redeemed_at
          ? new Date(row.original.redeemed_at).toLocaleString("en-PH", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.display_code}
          actions={
            row.original.status === "revalidation_required"
              ? [{ label: "Reactivate", onSelect: () => handleReactivatePass(row.original.id) }]
              : [
                  {
                    label: "Copy pass ID",
                    onSelect: () => void navigator.clipboard.writeText(row.original.id),
                  },
                ]
          }
        />
      ),
    },
  ];

  if (batchesLoading) {
    return <p className="text-sm text-muted-foreground">Loading GiftPass batches…</p>;
  }

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        description="Generate, manage, and track GiftPass batches and passes."
        title="GiftPass Management"
      />

      <div className="grid grid-cols-3 gap-4">
        <StatTile layout="compact" label="Total Batches" icon={ChartBarIcon}>
          <p className="text-2xl font-semibold text-primary-dark">{batches?.length || 0}</p>
        </StatTile>
        <StatTile layout="compact" label="Active Passes" icon={ChartBarIcon}>
          <p className="text-2xl font-semibold text-primary-dark">
            {batches?.reduce((sum, b) => sum + b.active, 0) || 0}
          </p>
        </StatTile>
        <StatTile layout="compact" label="Total Passes" icon={ChartBarIcon}>
          <p className="text-2xl font-semibold text-primary-dark">
            {batches?.reduce((sum, b) => sum + b.totalPasses, 0) || 0}
          </p>
        </StatTile>
      </div>

      <AdminDataTable
        presentation="standalone"
        data={batches ?? []}
        columns={batchColumns}
        getRowId={(batch) => batch.id}
        searchText={(batch) => [batch.name, batch.status].join(" ")}
        searchPlaceholder="Search batches…"
        header={
          <>
            <Button onClick={openGenerate}>
              <PlusIcon weight="bold" />
              Generate New Batch
            </Button>
            {isSuper ? (
              <Button variant="outline" onClick={() => setImporting(true)}>
                Import printed cards
              </Button>
            ) : null}
          </>
        }
        facets={[
          {
            id: "status",
            label: "Status",
            options: [
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ],
            getValue: (batch) => batch.status,
          },
        ]}
        initialSorting={[{ id: "created", desc: true }]}
        exportFilename="giftpass-batches"
        exportColumns={[
          { header: "Name", value: (batch) => batch.name },
          { header: "Total passes", value: (batch) => batch.totalPasses },
          { header: "Active", value: (batch) => batch.active },
          { header: "Status", value: (batch) => batch.status },
          { header: "Created", value: (batch) => batch.createdAt },
        ]}
      />

      {selectedBatch ? (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-primary-dark">
              Passes <span className="font-normal text-muted-foreground">· {selectedLabel}</span>
            </h2>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setSelectedBatch(null)}>
                Close
              </Button>
            </div>
          </div>

          <AdminDataTable
            presentation="standalone"
            data={passes ?? []}
            columns={passColumns}
            getRowId={(pass) => pass.id}
            searchText={(pass) => [pass.display_code, pass.status].join(" ")}
            searchPlaceholder="Search pass code or status…"
            facets={[
              {
                id: "status",
                label: "Status",
                options: [
                  "active",
                  "redeemed",
                  "expired",
                  "invalidated",
                  "revalidation_required",
                ].map((status) => ({ value: status, label: status.replaceAll("_", " ") })),
                getValue: (pass) => pass.status,
              },
            ]}
            initialSorting={[{ id: "redeemed", desc: true }]}
            loading={passesLoading}
            onExport={downloadCodes}
            exportScopeLabel="Bearer codes — keep the CSV private"
          />
        </div>
      ) : null}

      <Dialog
        open={generating}
        onOpenChange={(open) => {
          if (!open && !createBatch.isPending) setGenerating(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate a GiftPass batch</DialogTitle>
            <DialogDescription>
              Each pass is worth ₱{GIFTPASS_VALUE_PHP}, has no expiry, and gets its own printed
              code. Codes are bearer cards — anyone holding one can use it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label htmlFor="gp-company" className="block text-sm font-medium">
                Company
              </label>
              <Input
                id="gp-company"
                type="text"
                maxLength={120}
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                placeholder="Who the passes are for"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="gp-campaign" className="block text-sm font-medium">
                Campaign
              </label>
              <Input
                id="gp-campaign"
                type="text"
                maxLength={120}
                value={form.campaign}
                onChange={(e) => setForm((f) => ({ ...f, campaign: e.target.value }))}
                placeholder="Optional if a company is given"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="gp-count" className="block text-sm font-medium">
                Number of passes
              </label>
              <Input
                id="gp-count"
                type="number"
                inputMode="numeric"
                min={1}
                max={GIFTPASS_BATCH_MAX}
                step={1}
                value={form.count}
                onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
                placeholder={`1–${GIFTPASS_BATCH_MAX}`}
              />
              <p className="text-xs text-muted-foreground">
                Enter a company or a campaign. Up to {GIFTPASS_BATCH_MAX} passes per batch.
              </p>
            </div>

            {createBatch.isError ? (
              <p className="text-sm text-destructive">{createBatch.error.message}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGenerating(false)}
              disabled={createBatch.isPending}
            >
              Cancel
            </Button>
            <Button onClick={submitGenerate} disabled={!formValid || createBatch.isPending}>
              {createBatch.isPending
                ? "Generating…"
                : formValid
                  ? `Generate ${count} pass${count === 1 ? "" : "es"}`
                  : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportPrintedDialog
        open={importing}
        onOpenChange={setImporting}
        onImported={(batchId) => setSelectedBatch(batchId)}
      />
    </div>
  );
}
